import { Paper, Stack, Radio, Group, Text, Badge, ActionIcon, Title, Button } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';
import { messages } from '../messages';
import { Add, Edit, Locked, Purchase, TrashCan } from '@carbon/icons-react';
import { useGetPaymentMethodsQuery } from 'api/subscriptionPlanEndpoints';
import { Loading } from '@carbon/react';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import EmptyState from 'Components/Common/EmptyState/EmptyState';
import { PaymentDetailsDto } from 'api/dtos/subscriptionPlanDtos';
import { AddPaymentMethodModal } from 'Screens/Dashboard/Subscription/PaymentMethods/AddPaymentMethodModal/AddPaymentMethodModal';
import { useEffect, useState } from 'react';
import EditPaymentMethodModal from 'Screens/Dashboard/Subscription/PaymentMethods/EditPaymentMethodModal/EditPaymentMethodModal';
import RemovePaymentMethodModal from 'Screens/Dashboard/Subscription/PaymentMethods/RemovePaymentMethodModal/RemovePaymentMethodModal';
import styles from './PaymentMethods.module.scss';

const PaymentMethodsList = ({
    paymentMethods,
    onRemoved,
}: {
    paymentMethods: PaymentDetailsDto[]
    onRemoved: (paymentMethodToken: string) => void;
}) => {
    const intl = useIntl();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | undefined>(paymentMethods[0]?.paymentToken);
    const [selectedEditPaymentMethod, setSelectedEditPaymentMethod] = useState<PaymentDetailsDto | null>(null);
    const [selectedRemovePaymentMethod, setSelectedRemovePaymentMethod] = useState<string | null>(null);

    const onSelect = (id: string) => {
        setSelectedPaymentMethod(id);
    };

    const onEdit = (method: PaymentDetailsDto) => {
        setSelectedEditPaymentMethod(method);
    };

    const onEditClose = () => {
        setSelectedEditPaymentMethod(null);
    };

    const onDelete = (id: string) => {
        setSelectedRemovePaymentMethod(id);
    };

    const onDeleteClose = () => {
        setSelectedRemovePaymentMethod(null);
    };

    const handleRemoved = () => {
        if (selectedRemovePaymentMethod) {
            onRemoved(selectedRemovePaymentMethod);
        }
        setSelectedRemovePaymentMethod(null);
    };

    useEffect(() => {
        if (paymentMethods.length === 0) {
            setSelectedPaymentMethod(undefined);
            return;
        }

        if (!paymentMethods.some((method) => method.paymentToken === selectedPaymentMethod)) {
            setSelectedPaymentMethod(paymentMethods[0].paymentToken);
        }
    }, [paymentMethods, selectedPaymentMethod]);

    return (
        <>
            <Radio.Group value={selectedPaymentMethod} onChange={onSelect}>
                <Stack gap="sm">
                    {paymentMethods.map((method) => (
                        <Paper key={method.paymentToken} p="sm" radius="sm" withBorder>
                            <Group justify="space-between" wrap="nowrap">
                                <Group 
                                    gap="sm"
                                    style={{ flex: 1, cursor: 'pointer' }}
                                    onClick={() => { onSelect(method.paymentToken); }}
                                >
                                    {/* <Radio value={method.paymentToken} /> */}
                                    <Purchase size={24} />
                                    <div>
                                        <Text fw={600}>
                                            {method.cardBrand.toUpperCase()}
                                            {' '}
                                            **** **** ****
                                            {' '}
                                            {method.lastFourDigits}
                                        </Text>
                                        <Text size="sm" c="dimmed">

                                            {intl.formatMessage(messages.expires)}
                                            :
                                            {' '}
                                            {method.expirationMonth}
                                            /
                                            {method.expirationYear}
                                        </Text>
                                    </div>
                                    {method.isDefault && (
                                        <Badge color="blue" variant="light">
                                            {intl.formatMessage(messages.default)}
                                        </Badge>
                                    )}
                                </Group>

                                <Group gap="xs">
                                    <ActionIcon variant="subtle" color="blue" onClick={() => { onEdit(method); }}>
                                        <Edit size={18} />
                                    </ActionIcon>
                                    <ActionIcon variant="subtle" color="red" onClick={() => { onDelete(method.paymentToken); }}>
                                        <TrashCan size={18} />
                                    </ActionIcon>
                                </Group>
                            </Group>
                        </Paper>
                    ))}
                </Stack>
            </Radio.Group>
            {/* To_do useElements can be created only once */}
            {selectedEditPaymentMethod != null && <EditPaymentMethodModal opened onClose={onEditClose} paymentMethod={selectedEditPaymentMethod} />}
            <RemovePaymentMethodModal
                onModalClose={onDeleteClose}
                onRemoved={handleRemoved}
                confirmationModalOpen={selectedRemovePaymentMethod !== null}
                paymentMethodToken={selectedRemovePaymentMethod ?? ''}
            />
        </>
    );
};

export const PaymentMethods = () => {
    const intl = useIntl();
    const { data, isFetching, isError, refetch } = useGetPaymentMethodsQuery();
    const [removedPaymentMethodTokens, setRemovedPaymentMethodTokens] = useState<string[]>([]);
    const paymentMethods = (data ?? []).filter(
        (method) => !removedPaymentMethodTokens.includes(method.paymentToken),
    );
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        setRemovedPaymentMethodTokens((tokens) => tokens.filter(
            (token) => (data ?? []).some((method) => method.paymentToken === token),
        ));
    }, [data]);

    if (isFetching) {
        return <Loading />;
    }

    if (isError) {
        return <ErrorActionState onClick={refetch} />;
    }

    // const onSetDefault = () => {};

    const onAdd = () => {
        setModalOpen(true);
    };

    const onClose = () => {
        setModalOpen(false);
    };

    const onRemoved = (paymentMethodToken: string) => {
        setRemovedPaymentMethodTokens((tokens) => (
            tokens.includes(paymentMethodToken) ? tokens : [...tokens, paymentMethodToken]
        ));
        refetch().then(() => undefined, () => undefined);
    };

    return (
        <>
            <Paper shadow="sm" p="xl" radius="md" withBorder>
                <Title order={2} size="h3" mb="md">
                    {intl.formatMessage(messages.paymentMethods)}
                </Title>

                <Stack gap="md">
                    {!paymentMethods.length && <EmptyState title={intl.formatMessage(paymentMethodsMessages.emptyState)} />}
                    {paymentMethods.length > 0 && <PaymentMethodsList paymentMethods={paymentMethods} onRemoved={onRemoved} />}

                    <Button
                        variant="outline"
                        leftSection={<Add size={16} />}
                        fullWidth
                        className={styles.addCardButton}
                        onClick={onAdd}
                    >
                        {intl.formatMessage(messages.addPaymentMethod)}
                    </Button>

                    <Group gap="xs" wrap="nowrap" align="flex-start" className={styles.securityInfo}>
                        <Locked size={16} className={styles.securityIcon} />
                        <Text size="xs" c="dimmed">
                            {intl.formatMessage(paymentMethodsMessages.securityInfo)}
                        </Text>
                    </Group>
                </Stack>
            </Paper>
            {modalOpen && <AddPaymentMethodModal opened={modalOpen} onClose={onClose} />}
        </>
    );
};

const paymentMethodsMessages = defineMessages({
    emptyState: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.PaymentMethods.emptyState',
        defaultMessage: 'Brak metod płatności',
    },
    securityInfo: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.PaymentMethods.securityInfo',
        defaultMessage: 'Dane płatnicze są zabezpieczone i wykorzystujemy je wyłącznie do realizacji płatności za subskrypcję. Nie musisz się martwić, nie przechowujemy pełnych danych karty.',
    },
});
